import { environment } from '../environments/environment';
import axios, { AxiosPromise } from 'axios';
import Venue from '../models/Venue';
import SessionRequest from '../models/SessionRequest';
import Session from '../models/Session';
import Order from '../models/Order';
import Payment from '../models/Payment';
import Customer from '../models/Customer';
import MyOrdersResponse from '../models/MyOrdersResponse';
import Slot from '../models/Slot';
import ArticleCategory from '../models/ArticleCategory';
import OptionGroup from '../models/OptionGroup';
import { isMongoId } from 'validator';

import { customerBody, orderBody, paymentBody } from '../models/Converter';
import PromoCode from '../models/PromoCode';
import { Information } from '../models/Information';
import { PaymentMethod } from '../enums/PaymentMethod';

const ORDER_SERVICE = '/v1/order/';
const GENERAL_SERVICE = '/v1/general/';
const SESSION_SERVICE = '/v1/session/';
const PAYMENT_SERVICE = '/v1/payment/';
const PAYMENT_SERVICE_V2 = '/v2/payment/';
const PAYMENT_SERVICE_V3 = '/v3/payment/';
const AUTH_SERVICE = '/v1/auth/';

const VENUE = 'venue/';

const SESSION_ENDPOINT = SESSION_SERVICE;
const VENUE_ENDPOINT = GENERAL_SERVICE + VENUE;
const ORDER_ENDPOINT = ORDER_SERVICE;
const SLOT_ENDPOINT = ORDER_ENDPOINT + 'slot/';
const RESET_PASSWORD_ENDPOINT = AUTH_SERVICE + 'customer/firebase/reset';
const SEND_EMAIL_VERIFICATION_ENDPOINT = AUTH_SERVICE + 'customer/firebase/verifyEmail';
const DELETE_ACCOUNT_ENDPOINT = AUTH_SERVICE + 'customer';
const MAINTENANCE_ENDPOINT = GENERAL_SERVICE + 'maintenance/status';
const sanitizeId = (id: string) => {
	if (!id.startsWith(environment.customerGroup + '_') && !isMongoId(id)) {
		return environment.customerGroup + '_' + id;
	}
	return id;
};

class ApiService {
	axios = axios.create();

	constructor() {
		this.axios.defaults.baseURL = environment.baseUrl;
	}

	public createSession(sessionRequest: SessionRequest): AxiosPromise<Session> {
		sessionRequest.userUid = sanitizeId(sessionRequest.userUid);
		return this.axios.post(SESSION_ENDPOINT, sessionRequest);
	}

	public createOrder(order: Order): AxiosPromise<Order> {
		return this.axios.post(ORDER_ENDPOINT, orderBody(order));
	}

	public getAllVenues(articles: boolean): AxiosPromise<Venue[]> {
		return this.axios.get(VENUE_ENDPOINT + `?customerGroup=${environment.customerGroup}&articles=${articles}`);
	}

	public getVenues(lat: number, lng: number, distance: number, articles: boolean): AxiosPromise<Venue[]> {
		return this.axios.get(
			VENUE_ENDPOINT + `?lat=${lat}&lng=${lng}&distance=${distance}&articles=${articles}&customerGroup=${environment.customerGroup}`
		);
	}

	public getVenuesBy(postalCode: string): AxiosPromise<Venue[]> {
		return this.axios.get(VENUE_ENDPOINT + '?deliveryPostalCode=' + postalCode);
	}

	public getLazyVenue(venueId: string): AxiosPromise<Venue> {
		return this.axios.get(VENUE_ENDPOINT + sanitizeId(venueId));
	}

	public getCategoriesWithArticles(venue: string, articles: boolean): AxiosPromise<ArticleCategory[]> {
		return this.axios.get(GENERAL_SERVICE + `articlecategory/?venue=${venue}&articles=${articles}`);
	}

	public getOptionGroupsByVenue(venue: string): AxiosPromise<OptionGroup[]> {
		return this.axios.get(GENERAL_SERVICE + 'articleoption/byvenue/' + venue);
	}

	public getCustomerOrders(userUid: string): AxiosPromise<MyOrdersResponse> {
		return this.axios.get(PAYMENT_SERVICE_V2 + 'customer/' + sanitizeId(userUid));
	}

	public async createPayment(payment: Payment, v2: boolean): Promise<Payment> {
		payment._id = undefined;
		try {
			return (await this.axios.post(PAYMENT_SERVICE_V3, paymentBody(payment))).data;
		} catch (e) {
			return (await this.axios.post(
				(v2 ? PAYMENT_SERVICE_V2 : PAYMENT_SERVICE + (payment.method === PaymentMethod.PAYPAL ? 'paypal' : '')), paymentBody(payment))).data;
		}
	}

	public async finishPaypalPayment(paypalOrderId: string, v2: boolean): Promise<Payment> {
		try {
			return (await this.axios.patch(PAYMENT_SERVICE_V3 + paypalOrderId + '/paypal/pay')).data;
		} catch (e) {
			return (await this.axios.patch((v2 ? PAYMENT_SERVICE_V2 : PAYMENT_SERVICE) + paypalOrderId + '/paypal/pay')).data;
		}
	}

	public getCustomer(userUid: string): AxiosPromise<Customer> {
		return this.axios.get(SESSION_SERVICE + 'customer/uuid/' + sanitizeId(userUid));
	}

	public getSlots(venue: string, from: string, to: string): AxiosPromise<Slot[]> {
		return this.axios.get(SLOT_ENDPOINT + venue + '?from=' + from + '&to=' + to);
	}

	public patchCustomer(userUid: string, customer: Customer): AxiosPromise<Customer> {
		return this.axios.patch(SESSION_ENDPOINT + 'customer/uuid/' + sanitizeId(userUid), customerBody(customer));
	}

	public createCustomer(customer: Customer): AxiosPromise<Customer> {
		return this.axios.post(SESSION_ENDPOINT + 'customer/', customerBody(customer));
	}

	public getOrder(order: string): AxiosPromise<Order> {
		return this.axios.get(ORDER_ENDPOINT + order);
	}

	public resetPassword(body: { email: string; customerGroup: string }): AxiosPromise {
		return this.axios.post(RESET_PASSWORD_ENDPOINT, body);
	}

	public sendEmailVerification(userUid: string): AxiosPromise {
		return this.axios.post(SEND_EMAIL_VERIFICATION_ENDPOINT, {
			userUid: sanitizeId(userUid),
		});
	}

	public deleteAccount(userUid: string): AxiosPromise {
		return this.axios.delete(DELETE_ACCOUNT_ENDPOINT + '/' + sanitizeId(userUid));
	}

	public applyPromoCode(order: Order): AxiosPromise<Order> {
		return this.axios.post(ORDER_ENDPOINT + 'promoCode', orderBody(order));
	}

	public getPromoCode(body: { code: string; venue: string }): AxiosPromise<PromoCode> {
		return this.axios.post(GENERAL_SERVICE + 'promoCode/code', body);
	}

	public isMaintenanceActive(): Promise<boolean> {
		return new Promise<boolean>(async (resolve, reject) => {
			try {
				resolve((await this.axios.get(MAINTENANCE_ENDPOINT)).data);
			} catch (e) {
				reject(e);
			}
		});
	}

	public isSubCardNumberValid(cardNumber: string): Promise<boolean> {
		return new Promise<boolean>(async (resolve, reject) => {
			try {
				resolve(await this.axios.get(ORDER_ENDPOINT + 'subcard/?cardNumber=' + cardNumber));
			} catch (e) {
				reject(e);
			}
		});
	}

	public getSubCardNumberOf(email: string): Promise<string> {
		return new Promise<string>(async (resolve, reject) => {
			try {
				resolve((await this.axios.get(ORDER_ENDPOINT + 'subcard/?email=' + email)).data.subcard);
			} catch (e) {
				reject(e);
			}
		});
	}

	public getSubCard(num: string): AxiosPromise<{ balance: number; subcard: string }> {
		return this.axios.get(ORDER_ENDPOINT + 'subcard/?cardNumber=' + num);
	}

	public getInformations(): AxiosPromise<Information[]> {
		return this.axios.post(GENERAL_SERVICE + 'information/url', {
			url: window.location.hostname === 'localhost' ? 'dev-subway.smoothr.de' : window.location.hostname,
		});
	}
}

export let Api = new ApiService();
export { PAYMENT_SERVICE_V3, sanitizeId };
